/**
 * TopbarPrimaryNav — die primäre Tab-Reihe im Topbar, verschiebbar wie
 * eine Browser-Lesezeichenleiste ("die wichtigsten Tabs dorthin stellen,
 * wo man sie will").
 *
 * `PointerSensor` mit einem `activationConstraint`-Abstand von 8px
 * unterscheidet Klick von Drag: ein normaler Klick navigiert ganz normal
 * über `<Link>`, erst eine tatsächliche Zeigerbewegung startet dnd-kits
 * Drag — dnd-kit unterdrückt danach automatisch das synthetische
 * Klick-Event, sodass kein versehentlicher Navigationswechsel passiert.
 * `KeyboardSensor` hält die Reihenfolge tastaturbedienbar (Tab + Pfeile +
 * Leertaste), analog zu den übrigen WCAG-konformen Steuerelementen hier.
 *
 * Reihenfolge wird erst nach Drag-Ende einmalig persistiert
 * (`onReorder`) — siehe `useMainNavRoutes().reorderPrimary` und
 * `src/features/navTabOrder.ts`.
 */

import { Link } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@/lib/utils';
import type { RouteModule } from '@/features/types';

interface TopbarPrimaryNavProps {
  routes: RouteModule[];
  isActive: (route: RouteModule) => boolean;
  onReorder: (nextIds: string[]) => void;
  className?: string;
}

function SortableTab({ route, active }: { route: RouteModule; active: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: route.id });
  const Icon = route.nav!.icon;

  return (
    <Link
      ref={setNodeRef}
      to={route.path}
      aria-current={active ? 'page' : undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex touch-none select-none items-center gap-1.5 py-5',
        isDragging && 'opacity-50',
        active
          ? 'border-b-2 border-primary font-semibold text-ink'
          : 'text-ink-tertiary transition-colors hover:text-ink',
      )}
      {...attributes}
      {...listeners}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {route.nav!.label}
    </Link>
  );
}

export function TopbarPrimaryNav({ routes, isActive, onReorder, className }: TopbarPrimaryNavProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = routes.map((r) => r.id);
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(arrayMove(ids, oldIndex, newIndex));
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={routes.map((r) => r.id)} strategy={horizontalListSortingStrategy}>
        <div className={cn('flex items-center gap-7', className)}>
          {routes.map((route) => (
            <SortableTab key={route.id} route={route} active={isActive(route)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
